import * as React from 'react';

import {
    BLOCK_PARAGRAPH,
    type BlockTableData,
    moveToNextLeafBlock,
    type TableView,
    table,
} from '@gitbook/doc-core';
import {
    type Block,
    type Change,
    type Fragment,
    isTextRangeSelection,
    type TextRange,
} from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';

import {
    isBackspace,
    isDelete,
    isEnter,
    isModEnter,
    isModMinus,
    isOptionEnter,
    isOptionShiftEnter,
    isShiftTab,
    isTab,
} from '../common';
import { definitionSpecs, type TableDefinitionOnKeyDownKey } from './ColumnDefinitions';
import { TableStatesContext } from './contexts';
import type { TableState, TableStates } from './types';
import { viewSpecs } from './Views';

/**
 * Plugin to handle keyboard interactions in the table.
 *
 * Keyboard management depends on the current view.
 * The plugin takes care of filtering events and calling the view handler
 */
export function useTablePlugin(): [Plugin] {
    // Currently using a shared context that is not reset. It could be improved to define the context in ContentEditor.
    const tableStates = React.useContext(TableStatesContext);

    const plugin = React.useMemo<Plugin>(() => {
        return { onKeyDown: (event, change) => onKeyDown(tableStates, event, change) };
    }, [tableStates]);

    return [plugin];
}

function onKeyDown(states: TableStates, event: KeyboardEvent, change: Change) {
    // Filter ASAP on the event that we handle
    if (
        event.key !== 'ArrowUp' &&
        event.key !== 'ArrowDown' &&
        event.key !== 'ArrowRight' &&
        event.key !== 'ArrowLeft' &&
        !isEnter(event) &&
        !isModEnter(event) &&
        !isBackspace(event) &&
        !isDelete(event) &&
        !isTab(event) &&
        !isShiftTab(event) &&
        !isOptionEnter(event) &&
        !isOptionShiftEnter(event) &&
        !isModMinus(event)
    ) {
        return;
    }

    // Extended range operation are a specific case
    const { selection } = change.value;

    // Ignore table controls when selecting blocks only
    if (!isTextRangeSelection(selection)) {
        return;
    }

    if (!selection.isCollapsed && !isTab(event) && !isShiftTab(event)) {
        return;
    }

    // Only handle event in a table
    const currentTable = table.getCurrentTable(change.value);
    if (!currentTable) {
        return;
    }
    const { table: tableBlock, fragment } = currentTable;

    if (currentTable.ancestorBlocks && currentTable.ancestorBlocks.size > 0) {
        // If selection is inside a nested block in the table (for example a list)
        // We don't handle the event and let the other blocks handle it.
        return;
    }

    // Exit the table when pressing "Command+Enter" or "Ctrl+Enter"
    if (isModEnter(event)) {
        event.preventDefault();

        moveToNextLeafBlock(change, tableBlock);
        return change;
    }

    // Table state is filled at the rendering of the table
    // We only handle event for tables with a state defined and a selected record and/or definition
    const tableState = states.get(tableBlock.key);
    if (!tableState || (!tableState.selection.recordId && !tableState.selection.definitionId)) {
        return;
    }

    const tableData = tableBlock.data.toFrozenObject() as BlockTableData;

    const selectedRecord = tableState.selection.recordId
        ? tableData.records[tableState.selection.recordId]
        : null;
    const selectedDefinition = tableState.selection.definitionId
        ? tableData.definition[tableState.selection.definitionId]
        : null;

    const viewSpec = viewSpecs[tableState.view.type];
    const definitionSpec = selectedDefinition ? definitionSpecs[selectedDefinition.type] : null;
    const currentIndex = tableState.records.findIndex(
        (entry) => entry.key === tableState.selection.recordId
    );

    // Handle row insertion shortcuts
    if (isOptionEnter(event) || isOptionShiftEnter(event)) {
        event.preventDefault();

        if (isOptionShiftEnter(event)) {
            insertNewRow('above', change, tableBlock, tableState, currentIndex);
        } else {
            insertNewRow('below', change, tableBlock, tableState, currentIndex);
        }

        return change;
    }

    /** Run the event by the column definition (handling at the "cell level") */
    const runDefinitionOnKeyDown = (key: TableDefinitionOnKeyDownKey): boolean => {
        if (
            !definitionSpec ||
            !definitionSpec.onKeyDown ||
            !selectedRecord ||
            !selectedDefinition
        ) {
            return false;
        }

        let handled = false;
        definitionSpec.onKeyDown(key, selectedRecord.values[selectedDefinition.id], (newValue) => {
            handled = true;
            table.setRecordValue(
                change,
                tableBlock.key,
                tableState.selection.recordId!,
                tableState.selection.definitionId!,
                newValue
            );
        });

        return handled;
    };

    /** Run the event by the view definition (handling at the "grid level") */
    const runViewOnKeyDown = (key: Parameters<typeof viewSpec.onKeyDown>[0]) => {
        if (!viewSpec.onKeyDown) {
            return;
        }
        viewSpec.onKeyDown(key, tableBlock, tableState, change);
    };

    if (isEnter(event)) {
        if (fragment && !selection.hasAnchorAtEndOf(fragment)) {
            return;
        }

        event.preventDefault();

        // Move to next row in the same column
        const currentIndex = tableState.records.findIndex(
            (entry) => entry.key === tableState.selection.recordId
        );
        const isLastRow = currentIndex === tableState.records.length - 1;

        if (isLastRow) {
            insertNewRow('below', change, tableBlock, tableState, currentIndex);
        } else {
            // Move to next row, same column
            const nextRecordEntry = tableState.records[currentIndex + 1];

            table.moveTableSelection(change, tableBlock.key, {
                recordId: nextRecordEntry.key,
                definitionId: tableState.selection.definitionId,
            });
        }

        return change;
    }

    if (isBackspace(event)) {
        if (fragment && !selection.hasAnchorAtStartOf(fragment)) {
            return;
        }

        // If the cell fragment is empty, we let the table handle the backspace -
        // to move to next cell or delete the row.
        // However, if the fragment contains a non-paragraph block (for example a heading)
        // we let the editor handle the backspace to delete the heading and replace
        // it with an empty paragraph.
        if (fragment?.isEmpty && fragment?.nodes.first()?.type !== BLOCK_PARAGRAPH) {
            return;
        }

        event.preventDefault();

        if (!runDefinitionOnKeyDown('backspace')) {
            runViewOnKeyDown('backspace');
        }
        return change;
    }

    // If `cmd` + `-`, delete the row
    if (isModMinus(event)) {
        event.preventDefault();
        runViewOnKeyDown('mod+-');
        return change;
    }

    if (isTab(event) || isShiftTab(event)) {
        event.preventDefault();
        runViewOnKeyDown(isShiftTab(event) ? 'shift+tab' : 'tab');
        return change;
    }

    if (event.key === 'ArrowDown') {
        if (fragment && !isLastBlockOfFragment(fragment, selection)) {
            return;
        }

        event.preventDefault();
        runViewOnKeyDown('move-down');
        return change;
    }

    if (event.key === 'ArrowUp') {
        if (fragment && !isFirstBlockOfFragment(fragment, selection)) {
            return;
        }

        event.preventDefault();
        runViewOnKeyDown('move-up');
        return change;
    }

    if (event.key === 'ArrowRight') {
        if (fragment && !selection.hasAnchorAtEndOf(fragment)) {
            return;
        }

        event.preventDefault();
        runViewOnKeyDown('move-right');
        return change;
    }

    if (event.key === 'ArrowLeft') {
        if (fragment && !selection.hasAnchorAtStartOf(fragment)) {
            return;
        }

        event.preventDefault();
        runViewOnKeyDown('move-left');
        return change;
    }

    return change;
}

/** Test if selection is in the first block of the fragment */
function isFirstBlockOfFragment(fragment: Fragment, selection: TextRange): boolean {
    const firstBlock = fragment.nodes.first();
    return firstBlock ? selection.hasAnchorIn(firstBlock) : false;
}

/** Test if selection is in the last block of the fragment */
function isLastBlockOfFragment(fragment: Fragment, selection: TextRange): boolean {
    const lastBlock = fragment.nodes.last();
    return lastBlock ? selection.hasAnchorIn(lastBlock) : false;
}

function insertNewRow(
    direction: 'above' | 'below',
    change: Change,
    tableBlock: Block,
    tableState: TableState<TableView>,
    currentIndex: number
) {
    const currentRecordEntry = tableState.records[currentIndex];

    if (direction === 'above') {
        const beforeRecordEntry = tableState.records[currentIndex - 1];

        const newRecordId = table.insertEmptyRecord(change, tableBlock.key, {
            before: beforeRecordEntry?.value.orderIndex,
            after: currentRecordEntry.value.orderIndex,
        });

        table.moveTableSelection(change, tableBlock.key, {
            recordId: newRecordId,
            definitionId: tableState.selection.definitionId,
        });
    } else {
        const afterRecordEntry = tableState.records[currentIndex + 1];

        const newRecordId = table.insertEmptyRecord(change, tableBlock.key, {
            before: currentRecordEntry.value.orderIndex,
            after: afterRecordEntry?.value.orderIndex,
        });

        table.moveTableSelection(change, tableBlock.key, {
            recordId: newRecordId,
            definitionId: tableState.selection.definitionId,
        });
    }
}
